import type { IconComponent } from "reicon-react/createIcon";
import {
	AtSign,
	BadgePercent,
	Bill,
	BookOpen,
	Briefcase,
	Buildings,
	Calendar,
	CalendarTick,
	Car,
	Card,
	CardTransfer,
	Cart,
	ChartBar,
	ChevronDown,
	ClipboardCheck,
	ClipboardList,
	Clock,
	CreditCard,
	Database,
	Document,
	DocumentText,
	Envelope,
	FileText,
	Folder,
	Gear,
	Handshake,
	Home,
	Inbox,
	Invoice,
	Layers,
	Link as LinkIcon,
	LockPassword,
	Money,
	MoneyBill,
	MoneySend,
	Note,
	Package,
	Phone,
	Profile,
	Receipt,
	Scale,
	Settings,
	Share,
	Sms,
	Tag,
	Task,
	UserCircle,
	Users,
	Wallet,
	Window,
} from "reicon-react";
import type { ReactNode } from "react";
import type { WorkspaceApp } from "@bafe/types";
import { Link, NavLink } from "react-router";

import { workspaceSectionActive } from "../lib/platform-navigation.js";
import { webmailInboxUrl } from "../lib/webmail.js";
import { workspaceHref } from "../lib/workspace-url.js";
import { MY_HUB_PAGES } from "../routes/shell/RecordPages";

export function UserNav({ pathname }: { pathname: string }) {
	const settingsActive = pathname === "/user/settings" || pathname.startsWith("/user/settings/");
	return (
		<>
			<Item to="/user/workspaces" label="Workspace" icon={Buildings} end={pathname === "/user/workspaces"} />
			<Item to="/user/billing" label="Billing" icon={CreditCard} end={pathname === "/user/billing"} />
			<Item to="/user/users" label="Users" icon={Users} end={pathname === "/user/users"} />
			<Link
				className={settingsActive ? "nav-link active" : "nav-link"}
				to="/user/settings/profile"
			>
				<Settings size={ICO} aria-hidden="true" />
				<span>Profile & Settings</span>
			</Link>
		</>
	);
}

export function PlatformWorkspaceNav({ pathname }: { pathname: string }) {
	const rest = pathname.split("?")[0] ?? pathname;
	return (
		<>
			<Item to="/platform/workspace" label="Workspace" icon={Buildings} end={rest === "/platform/workspace"} />
			<Item to="/platform/users" label="Users" icon={Users} />
			<Item to="/platform/billing" label="Billing" icon={CreditCard} />
		</>
	);
}

export function ClientNav({ workspaceSlug }: { workspaceSlug: string }) {
	const ws = (path: string) => workspaceHref(workspaceSlug, path);
	return (
		<>
			<Item to={ws("client")} label="Dashboard" icon={Home} end />
			<Item to={ws("client/work")} label="Work" icon={Briefcase} />
			<Item to={ws("client/calendar")} label="Calendar" icon={Calendar} />
			<Item to={ws("client/quotations")} label="Quotations" icon={DocumentText} />
			<Item to={ws("client/invoices")} label="Invoices" icon={Invoice} />
		</>
	);
}

export function PhoneNav({ workspaceSlug }: { workspaceSlug: string }) {
	const ws = (path: string) => workspaceHref(workspaceSlug, path);
	return (
		<>
			<Item to={ws("phone/calls")} label="Calls" icon={Phone} />
			<Item to={ws("phone/sms")} label="SMS" icon={Sms} />
		</>
	);
}

const ICO = 16;

function Item({
	to,
	href,
	label,
	icon: Icon,
	end,
}: {
	to?: string;
	href?: string;
	label: string;
	icon: IconComponent;
	end?: boolean;
}) {
	if (href) {
		return (
			<a href={href} className="nav-link">
				<Icon size={ICO} aria-hidden="true" />
				<span>{label}</span>
			</a>
		);
	}
	return (
		<NavLink to={to!} end={end} className={({ isActive }) => (isActive ? "nav-link active" : "nav-link")}>
			<Icon size={ICO} aria-hidden="true" />
			<span>{label}</span>
		</NavLink>
	);
}

function Group({
	label,
	icon: Icon,
	pathname,
	items,
}: {
	label: string;
	icon: IconComponent;
	pathname: string;
	items: Array<{ to: string; label: string; icon: IconComponent }>;
}) {
	const open = items.some((item) => pathname === item.to || pathname.startsWith(`${item.to}/`));
	return (
		<details className="nav-drop" open={open}>
			<summary className="nav-link">
				<Icon size={ICO} aria-hidden="true" />
				<span>{label}</span>
				<ChevronDown size={12} aria-hidden="true" />
			</summary>
			<div className="nav-sub">
				{items.map((item) => (
					<Item key={item.to} to={item.to} label={item.label} icon={item.icon} />
				))}
			</div>
		</details>
	);
}

const MY_ICONS: Record<string, IconComponent> = {
	"my/tasks": Task,
	"my/profile": UserCircle,
	"my/claims": ClipboardList,
	"my/payslip": MoneyBill,
	"my/ea-form": FileText,
	"my/leave": CalendarTick,
	"my/attendance": Clock,
};

export function HubNav({
	pathname,
	workspaceSlug,
	viewAllTasks = true,
	showStaff = true,
}: {
	pathname: string;
	workspaceSlug: string;
	viewAllTasks?: boolean;
	showStaff?: boolean;
}) {
	const ws = (path: string) => workspaceHref(workspaceSlug, path);
	const navPath = pathname.includes("/workspace/")
		? pathname.replace(/^.*\/workspace\/[^/]+/, "")
		: pathname;
	return (
		<>
			<Group
				label="My"
				icon={Profile}
				pathname={navPath}
				items={MY_HUB_PAGES.map((page) => ({
					to: ws(`hub/${page.path}`),
					label: page.title,
					icon: MY_ICONS[page.path] ?? UserCircle,
				}))}
			/>
			{showStaff ? (
				<Item
					to={ws("")}
					label="Overview"
					icon={Home}
					end={navPath === "/" || navPath === "/hub"}
				/>
			) : null}
			{viewAllTasks ? <Item to={ws("hub/tasks")} label="Task" icon={Task} /> : null}
			{showStaff ? (
				<>
			<Item to={ws("hub/calendar")} label="Calendar" icon={Calendar} />
			<Item to={ws("hub/organisation")} label="Organisation" icon={Buildings} />
			<Group
				label="Operation"
				icon={Briefcase}
				pathname={navPath}
				items={[
					{ to: ws("hub/inventory"), label: "Inventory", icon: Package },
					{ to: ws("hub/consumables"), label: "Consumables", icon: Layers },
					{ to: ws("hub/customer-sites"), label: "Site", icon: Buildings },
					{ to: ws("hub/projects"), label: "Project", icon: Folder },
					{ to: ws("hub/work-orders"), label: "Work Order", icon: Briefcase },
				]}
			/>
			<Group
				label="Sales"
				icon={Handshake}
				pathname={navPath}
				items={[
					{ to: ws("hub/leads"), label: "Leads", icon: Users },
					{ to: ws("hub/deals"), label: "Deals", icon: Handshake },
					{ to: ws("hub/items"), label: "Item", icon: Tag },
					{ to: ws("hub/quotations"), label: "Quotation", icon: DocumentText },
					{ to: ws("hub/sales-orders"), label: "Sales Order", icon: ClipboardList },
					{ to: ws("hub/invoices"), label: "Invoice", icon: Invoice },
					{ to: ws("hub/receipts"), label: "Receipts", icon: Receipt },
				]}
			/>
			<Group
				label="Purchases"
				icon={Cart}
				pathname={navPath}
				items={[
					{ to: ws("hub/expenses"), label: "Expenses", icon: MoneySend },
					{ to: ws("hub/bills"), label: "Bills", icon: Bill },
					{ to: ws("hub/purchase-orders"), label: "Purchase order", icon: Cart },
					{ to: ws("hub/credits"), label: "Credits", icon: Card },
				]}
			/>
			<Group
				label="People"
				icon={Users}
				pathname={navPath}
				items={[
					{ to: ws("hub/leave"), label: "Leave", icon: CalendarTick },
					{ to: ws("hub/attendance"), label: "Attendance", icon: Clock },
					{ to: ws("hub/claims"), label: "Claims", icon: ClipboardList },
					{ to: ws("hub/payroll"), label: "Payroll", icon: Money },
					{ to: ws("hub/people-settings"), label: "People Settings", icon: Settings },
				]}
			/>
			<Group
				label="Finance"
				icon={Wallet}
				pathname={navPath}
				items={[
					{ to: ws("hub/internal"), label: "Internal", icon: Buildings },
					{ to: ws("hub/transactions"), label: "Transactions", icon: CardTransfer },
					{ to: ws("hub/journals"), label: "Journals", icon: BookOpen },
					{ to: ws("hub/accounts"), label: "Charts of accounts", icon: Scale },
					{ to: ws("hub/tax"), label: "Tax", icon: BadgePercent },
					{ to: ws("hub/audit"), label: "Audit", icon: ClipboardCheck },
					{ to: ws("hub/assets"), label: "Asset", icon: Buildings },
				]}
			/>
			<Group
				label="Admin"
				icon={Gear}
				pathname={navPath}
				items={[
					{ to: ws("hub/maintenance"), label: "Maintenance", icon: Settings },
					{ to: ws("hub/letters"), label: "Letters", icon: Sms },
					{ to: ws("hub/repairs"), label: "Repairs", icon: Gear },
					{ to: ws("hub/register"), label: "Register", icon: ClipboardList },
					{ to: ws("hub/credentials"), label: "Credentials", icon: LockPassword },
					{ to: ws("hub/documents"), label: "Documents", icon: Document },
					{ to: ws("hub/vehicles"), label: "Vehicle", icon: Car },
					{ to: ws("hub/phones"), label: "Phone number", icon: Phone },
				]}
			/>
			<Item to={ws("hub/reports")} label="Reports" icon={ChartBar} />
				</>
			) : null}
		</>
	);
}

export function MailNav({ workspaceSlug }: { workspaceSlug: string }) {
	const ws = (path: string) => workspaceHref(workspaceSlug, path);
	return (
		<>
			<Item to={ws("mail")} label="Overview" icon={Home} end />
			<Item href={webmailInboxUrl(workspaceSlug)} label="Mail" icon={Envelope} />
		</>
	);
}

export function SiteNav({ workspaceSlug }: { workspaceSlug: string }) {
	const ws = (path: string) => workspaceHref(workspaceSlug, path);
	return (
		<>
			<Item to={ws("site")} label="Overview" icon={Home} end />
			<Item to={ws("site/sites")} label="Sites" icon={Window} />
			<Item to={ws("site/databases")} label="Databases" icon={Database} />
			<Item to={ws("site/redirects")} label="Redirects" icon={LinkIcon} />
		</>
	);
}

export function TeamNav({ workspaceSlug }: { workspaceSlug: string }) {
	const ws = (path: string) => workspaceHref(workspaceSlug, path);
	return (
		<>
			<Item to={ws("team")} label="Conversations" icon={Sms} />
		</>
	);
}

export function SocialNav({ workspaceSlug }: { workspaceSlug: string }) {
	const ws = (path: string) => workspaceHref(workspaceSlug, path);
	return (
		<>
			<Item to={ws("social")} label="Overview" icon={Home} end />
			<Item to={ws("social/channels")} label="Channels" icon={Share} />
			<Item to={ws("social/calendar")} label="Calendar" icon={Calendar} />
			<Item to={ws("social/compose")} label="Compose" icon={Note} />
			<Item to={ws("social/results")} label="Results" icon={ChartBar} />
		</>
	);
}

export function EngageNav({ workspaceSlug }: { workspaceSlug: string }) {
	const ws = (path: string) => workspaceHref(workspaceSlug, path);
	return (
		<>
			<Item to={ws("engage/inbox")} label="Inbox" icon={Inbox} />
			<Item to={ws("engage/accounts")} label="Connect" icon={AtSign} />
		</>
	);
}

function NavSection({
	label,
	icon: Icon,
	open,
	children,
}: {
	label: string;
	icon: IconComponent;
	open: boolean;
	children: ReactNode;
}) {
	return (
		<details className="nav-drop nav-context-section" open={open}>
			<summary className="nav-link">
				<Icon size={ICO} aria-hidden="true" />
				<span>{label}</span>
				<ChevronDown size={12} aria-hidden="true" />
			</summary>
			<div className="nav-sub">{children}</div>
		</details>
	);
}

export function WorkspaceSidebarNav({
	pathname,
	workspaceSlug,
	apps,
	mailOnly = false,
	viewAllHubTasks = true,
	showHubStaff = true,
}: {
	pathname: string;
	workspaceSlug: string;
	apps: Set<WorkspaceApp>;
	mailOnly?: boolean;
	viewAllHubTasks?: boolean;
	showHubStaff?: boolean;
}) {
	if (mailOnly) {
		return (
			<>
				<MailNav workspaceSlug={workspaceSlug} />
				<Item to="/user/settings/profile" label="Account" icon={Settings} />
			</>
		);
	}

	return (
		<>
			{apps.has("hub") ? (
				<NavSection label="Hub" icon={Briefcase} open={workspaceSectionActive(pathname, "hub")}>
					<HubNav
						pathname={pathname}
						workspaceSlug={workspaceSlug}
						viewAllTasks={viewAllHubTasks}
						showStaff={showHubStaff}
					/>
				</NavSection>
			) : null}
			{apps.has("hub") ? (
				<NavSection label="Client" icon={Handshake} open={workspaceSectionActive(pathname, "client")}>
					<ClientNav workspaceSlug={workspaceSlug} />
				</NavSection>
			) : null}
			{apps.has("mail") ? (
				<NavSection label="Mail" icon={Envelope} open={workspaceSectionActive(pathname, "mail")}>
					<MailNav workspaceSlug={workspaceSlug} />
				</NavSection>
			) : null}
			{apps.has("team") ? (
				<NavSection label="Team" icon={Users} open={workspaceSectionActive(pathname, "team")}>
					<TeamNav workspaceSlug={workspaceSlug} />
				</NavSection>
			) : null}
			{apps.has("hub") ? (
				<NavSection label="Phone" icon={Phone} open={workspaceSectionActive(pathname, "phone")}>
					<PhoneNav workspaceSlug={workspaceSlug} />
				</NavSection>
			) : null}
			{apps.has("engage") ? (
				<NavSection label="Engage" icon={Inbox} open={workspaceSectionActive(pathname, "engage")}>
					<EngageNav workspaceSlug={workspaceSlug} />
				</NavSection>
			) : null}
			{apps.has("social") ? (
				<NavSection label="Social" icon={Share} open={workspaceSectionActive(pathname, "social")}>
					<SocialNav workspaceSlug={workspaceSlug} />
				</NavSection>
			) : null}
			{apps.has("site") ? (
				<NavSection label="Site" icon={Window} open={workspaceSectionActive(pathname, "site")}>
					<SiteNav workspaceSlug={workspaceSlug} />
				</NavSection>
			) : null}
		</>
	);
}
